import { fetchSystemInfo } from '@/api/modules/system'
import { fetchSnailmailInfo } from '@/api/modules/delivery'

const SystemConfig = function(config) {
	const self = this
	self.company = config.company
	self.trips = config.trips
	self.stops = config.stops
	self.payments = config.paymentProviders
	self.stringCharacterSets = config.stringCharacterSets
	self.snailmailDelivery = config.snailmailDelivery
	self.webshop = config.webshop

	self.dsbStandardTicketProducts = config.products.dsbStandardTicketProducts
	self.dsbSeatTicketProducts = config.products.dsbSeatTicketProducts
	self.dsbOrangeTicketProducts = config.products.dsbOrangeTicketProducts
	self.fixedPriceTicketProducts = config.products.fixedPriceTicketProducts
	self.orangeTicketProducts = config.products.orangeTicketProducts
	self.seatTicketProducts = config.products.seatTicketProducts
	self.timeTicketProducts = config.products.timeTicketProducts
	self.zoneTicketProducts = config.products.zoneTicketProducts
	self.retireeTicketProducts = config.products.retireeTicketProducts
}

export const state = {
	system: null,
	snailmail: null,
	stopProductTypes: []
}

export const mutations = {
	updateSystemConfig: (state, payload) => {
		state.system = new SystemConfig(payload)
	},
	updateSnailmailConfig: (state, payload) => {
		state.snailmail = payload
	},
	addStopProductType: (state, payload) => {
		state.stopProductTypes.push(payload.productType)
	}
}

export const getters = {
	getMaxStopSearchLength: state => {
		return state.system ? state.system.stops.stopNameMaxLength : null
	},
	getStopProductTypes: state => {
		return state.stopProductTypes
	},
	getDibsMerchantId: state => {
		return state.system ? state.system.payments.dibs.merchantId : null
	},
	getSecondaryWebshop: state => {
		return state.system ? state.system.webshop.secondaryWebshops : null
	},
	getEpayMerchantId: state => {
		return state.system && state.system.payments.epay ? state.system.payments.epay.merchantId : null
	},
	getNetaxeptMerchantId: state => {
		try {
			return state.system.payments.netaxept.clientMerchantId.webshopMerchantId
		} catch (e) {
			return null
		}
	},
	// helper for mapping a product type identifier to a vuex module
	enabledProductsModuleMapping: (state, getters, rootState) => {
		const mapping = {}
		if (rootState.products) {
			Object.keys(rootState.products).forEach(key => {
				if (rootState.products[key].info) mapping[rootState.products[key].info.identifier] = key
			})
		}
		return mapping
	},
	getDepartureInstantThresholdMinutes: state => {
		return state.system && state.system.trips ? state.system.trips.orderingDepartureInstantThresholdMinutes : 0
	},
	alphaNumericCharacterSet: state => {
		return state.system && state.system.stringCharacterSets.alphaNumeric.split('')
	},
	commonCharacterSet: state => {
		return state.system && state.system.stringCharacterSets.common.split('')
	},
	mailAddressCharacterSet: state => {
		return state.system && state.system.stringCharacterSets.mailAddress.split('')
	},
	snailmailShipmentDelayDayCount: ({ snailmail }) => snailmail && snailmail.shipmentDelayDayCount,
	snailmailShipmentFeeInOre: ({ snailmail }) => snailmail && snailmail.shipmentFeeInOre,
	isZoneTicketProductsEnabled: state => {
		return state.system && state.system.zoneTicketProducts && state.system.zoneTicketProducts.enabled
	}
}

export const actions = {
	loadSnailmail: async ({ commit }) => {
		const response = await fetchSnailmailInfo()

		if (!response.ok) {
			throw new Error(JSON.stringify(response.responseBody))
		}

		commit('updateSnailmailConfig', response.responseBody)
	},
	loadConfig: async ({ dispatch, commit, state }) => {
		const response = await fetchSystemInfo()

		if (!response.ok) {
			throw new Error(JSON.stringify(response.responseBody))
		}

		commit('updateSystemConfig', response.responseBody)

		const system = state.system

		const promises = [
			dispatch('passenger/fetchPassengerTypeList', null, { root: true }),
			dispatch('identification/updateIdentificationTypes', null, {
				root: true
			}),
			dispatch('date/setServerTime', { setDepartureDate: true }, { root: true })
		]

		if (system.zoneTicketProducts.enabled) {
			promises.push(dispatch('products/zoneticket/fetchInfo', null, { root: true }))
		}

		if (system.fixedPriceTicketProducts.enabled) {
			promises.push(
				dispatch('products/fixedpriceticket/fetchInfo', null, {
					root: true
				})
			)
		}

		if (system.dsbStandardTicketProducts.enabled) {
			promises.push(
				dispatch('products/dsbstandardticket/fetchInfo', null, {
					root: true
				})
			)
		}

		if (system.dsbSeatTicketProducts.enabled) {
			promises.push(
				dispatch('products/dsbseatticket/fetchInfo', null, {
					root: true
				})
			)
		}

		if (system.dsbOrangeTicketProducts.enabled) {
			promises.push(
				dispatch('products/dsborangeticket/fetchInfo', null, {
					root: true
				})
			)
		}

		if (system.orangeTicketProducts.enabled) {
			promises.push(
				dispatch('products/orangeticket/fetchInfo', null, {
					root: true
				})
			)
		}

		if (system.seatTicketProducts.enabled) {
			promises.push(
				dispatch('products/seatticket/fetchInfo', null, {
					root: true
				})
			)
		}

		if (system.timeTicketProducts.enabled) {
			promises.push(dispatch('products/timeticket/fetchInfo', null, { root: true }))
		}

		if (system.retireeTicketProducts.enabled) {
			promises.push(
				dispatch('products/retireeticket/fetchInfo', null, {
					root: true
				})
			)
		}

		if (system.seatTicketProducts.enabled || system.dsbSeatTicketProducts) {
			promises.push(dispatch('seats/fetchSeatTypes', null, { root: true }))
		}

		if (system.snailmailDelivery.enabled) {
			promises.push(dispatch('loadSnailmail'))
		}

		await Promise.all(promises)

		// Only do this if popular stops is enabled
		// We depend on stop types from the above promisses.
		if (process.env.VUE_APP_SHOW_POPULAR_STOP_RESULTS === 'true') {
			await dispatch('location/searchPopularStops', null, { root: true })
		}

		return true
	}
}
